/**
 * Trunnion AI - Approval Item Component
 * Single approval request with description, expiry countdown, and approve/deny actions.
 */

import { useState, useEffect, useCallback } from 'react';
import { Clock, Check, Ban } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNotificationStore, type ApprovalQueueItem } from '@/stores/notificationStore';
import { executionsApi } from '@/api/executions.api';
import { ROUTES } from '@/config/routes';

/**
 * Calculates the remaining time until expiry and formats it as a human-readable string.
 */
function getTimeRemaining(expiresAt: string): string {
  const now = Date.now();
  const expiry = new Date(expiresAt).getTime();
  const diffMs = expiry - now;

  if (diffMs <= 0) return 'Expired';

  const diffMinutes = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMinutes / 60);

  if (diffMinutes < 1) return 'Expires in <1m';
  if (diffMinutes < 60) return `Expires in ${diffMinutes}m`;
  if (diffHours < 24) {
    const remainingMinutes = diffMinutes % 60;
    return remainingMinutes > 0
      ? `Expires in ${diffHours}h ${remainingMinutes}m`
      : `Expires in ${diffHours}h`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return `Expires in ${diffDays}d`;
}

/**
 * Truncates a UUID-style string for display.
 */
function truncateId(id: string): string {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}...`;
}

interface ApprovalItemProps {
  item: ApprovalQueueItem;
}

export function ApprovalItem({ item }: ApprovalItemProps) {
  const { removeApprovalRequest, addNotification } = useNotificationStore();
  const [timeRemaining, setTimeRemaining] = useState(() =>
    getTimeRemaining(item.expiresAt),
  );
  const [loading, setLoading] = useState<'approve' | 'deny' | null>(null);

  // Update countdown every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(getTimeRemaining(item.expiresAt));
    }, 30_000);
    return () => clearInterval(interval);
  }, [item.expiresAt]);

  const isExpired = timeRemaining === 'Expired';

  const handleApprove = useCallback(async () => {
    setLoading('approve');
    try {
      await executionsApi.approve(item.executionId, item.stepId, 'Approved via notification center');
      removeApprovalRequest(item.id);
      addNotification({
        type: 'success',
        title: 'Step Approved',
        message: `Approved step for execution ${truncateId(item.executionId)}`,
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Approval Failed',
        message: error instanceof Error ? error.message : 'Failed to approve step',
      });
    } finally {
      setLoading(null);
    }
  }, [item, removeApprovalRequest, addNotification]);

  const handleDeny = useCallback(async () => {
    setLoading('deny');
    try {
      await executionsApi.deny(item.executionId, item.stepId, 'Denied via notification center');
      removeApprovalRequest(item.id);
      addNotification({
        type: 'info',
        title: 'Step Denied',
        message: `Denied step for execution ${truncateId(item.executionId)}`,
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Denial Failed',
        message: error instanceof Error ? error.message : 'Failed to deny step',
      });
    } finally {
      setLoading(null);
    }
  }, [item, removeApprovalRequest, addNotification]);

  return (
    <div className="border-b border-trunnion-slate-700/50 px-4 py-3 last:border-b-0">
      {/* Description */}
      <p className="text-sm font-medium text-white">{item.description}</p>

      {/* Execution link */}
      <div className="mt-1.5 flex items-center gap-3">
        <Link
          to={ROUTES.EXECUTION_DETAIL(item.executionId)}
          className="text-xs text-trunnion-gold-500 hover:text-trunnion-gold-400 transition-colors"
        >
          Execution: {truncateId(item.executionId)}
        </Link>

        {/* Expiry countdown */}
        <div
          className={`flex items-center gap-1 text-xs ${
            isExpired ? 'text-red-400' : 'text-trunnion-slate-400'
          }`}
        >
          <Clock className="h-3 w-3" />
          <span>{timeRemaining}</span>
        </div>
      </div>

      {/* Required roles */}
      {item.requiredRoles.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {item.requiredRoles.map((role) => (
            <span
              key={role}
              className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-trunnion-slate-700/50 text-trunnion-slate-400 border border-trunnion-slate-600/50"
            >
              {role}
            </span>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="mt-3 flex items-center gap-2">
        <button
          onClick={handleApprove}
          disabled={loading !== null || isExpired}
          className="flex items-center gap-1.5 rounded-md bg-green-500/10 px-3 py-1.5 text-xs font-medium text-green-400 border border-green-500/20 hover:bg-green-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Check className="h-3.5 w-3.5" />
          {loading === 'approve' ? 'Approving...' : 'Approve'}
        </button>
        <button
          onClick={handleDeny}
          disabled={loading !== null || isExpired}
          className="flex items-center gap-1.5 rounded-md bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-400 border border-red-500/20 hover:bg-red-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Ban className="h-3.5 w-3.5" />
          {loading === 'deny' ? 'Denying...' : 'Deny'}
        </button>
      </div>
    </div>
  );
}
